<script>
import axios from 'axios';
import { BmtEvolutionInboxesApi } from 'dashboard/api/bmtCampaigns';
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import PageHeader from '../../SettingsSubPageHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import router from '../../../../index';

const EVO_URL = 'https://evo.bruzap.com.br';
const EVO_KEY = import.meta.env.VITE_EVOLUTION_API_KEY || '';

export default {
  name: 'WhatsappQrChannel',
  components: { PageHeader, NextButton },
  data() {
    return {
      step: 'form',
      channelName: '',
      instanceName: '',
      inboxId: null,
      qrBase64: null,
      phoneNumber: '',
      countdown: 60,
      pollingInterval: null,
      countdownInterval: null,
      isCreating: false,
    };
  },
  computed: {
    ...mapGetters({ uiFlags: 'inboxes/getUIFlags', currentUser: 'getCurrentUser' }),
    slugName() {
      return this.channelName.toLowerCase()
        .replace(/[^a-z0-9]/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/, '');
    },
  },
  beforeDestroy() {
    this.stopPolling();
  },
  methods: {
    async createChannel() {
      if (!this.channelName.trim()) return;
      // FIX multi-tenant: usa a conta ATUAL da rota + token do usuario logado
      // (antes era hardcoded conta 1 + token do Bruno -> inbox vazava pra conta 1)
      const accountId = this.$route.params.accountId;
      const userToken = this.currentUser && this.currentUser.access_token;
      if (!accountId || !userToken) {
        useAlert(this.$t('INBOX_MGMT.ADD.WHATSAPP_QR_CHANNEL.ERROR'));
        return;
      }
      this.isCreating = true;
      try {
        // Use timestamp suffix for uniqueness
        const ts = Date.now().toString(36);
        const instanceSlug = this.slugName + '-' + ts;
        this.instanceName = instanceSlug;

        // Create Evolution instance - it will auto-create the Chatwoot inbox
        let createQr = null;
        const createResp = await axios.post(EVO_URL + '/instance/create', {
          instanceName: instanceSlug,
          qrcode: true,
          integration: 'WHATSAPP-BAILEYS',
          chatwootAccountId: String(accountId),
          chatwootToken: userToken,
          chatwootUrl: 'https://chat.bruzap.com.br',
          chatwootNameInbox: this.channelName.trim(),
          chatwootSignMsg: false,
          chatwootReopenConversation: true,
          chatwootConversationPending: false,
          chatwootImportContacts: true,
          chatwootImportMessages: false,
          groupsIgnore: true,
        }, { headers: { apikey: EVO_KEY } });

        // Extract QR from create response
        const qrData = createResp.data && createResp.data.qrcode;
        if (qrData) {
          let b64 = qrData.base64 || qrData.code || null;
          if (b64 && b64.startsWith('data:')) b64 = b64.split(',')[1];
          createQr = b64;
        }

        // Find the inbox created by Evolution in Chatwoot
        await new Promise(r => setTimeout(r, 2000));
        const inboxResp = await this.$store.dispatch('inboxes/get');
        const inboxes = this.$store.getters['inboxes/getInboxes'];
        const inbox = inboxes.find(i => i.name === this.channelName.trim() || (i.webhook_url && i.webhook_url.includes(instanceSlug)));
        this.inboxId = inbox ? inbox.id : null;

        if (createQr) {
          this.qrBase64 = createQr;
          this.resetCountdown();
        } else {
          await new Promise(r => setTimeout(r, 3000));
          await this.fetchQr();
        }
        this.step = 'qr';
        this.startPolling();
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.ADD.WHATSAPP_QR_CHANNEL.ERROR'));
      } finally {
        this.isCreating = false;
      }
    },
    async fetchQr() {
      for (let attempt = 0; attempt < 4; attempt++) {
        try {
          const resp = await axios.get(
            EVO_URL + '/instance/connect/' + this.instanceName,
            { headers: { apikey: EVO_KEY } }
          );
          let b64 = resp.data.base64 ||
            (resp.data.qrcode && (resp.data.qrcode.base64 || resp.data.qrcode.code)) ||
            resp.data.code ||
            null;
          if (b64 && b64.startsWith('data:')) b64 = b64.split(',')[1];
          if (b64) {
            this.qrBase64 = b64;
            this.resetCountdown();
            return;
          }
        } catch (e) { /* silent */ }
        if (attempt < 3) await new Promise(r => setTimeout(r, 3000));
      }
      this.qrBase64 = null;
    },
    async checkStatus() {
      try {
        const resp = await axios.get(
          EVO_URL + '/instance/fetchInstances',
          { headers: { apikey: EVO_KEY } }
        );
        const instances = resp.data;
        const inst = Array.isArray(instances)
          ? instances.find(i => i.name === this.instanceName || (i.instance && i.instance.instanceName === this.instanceName))
          : null;
        if (inst) {
          const state = (inst.instance && inst.instance.state) || inst.connectionStatus;
          if (state === 'open') {
            const owner = (inst.instance && inst.instance.owner) || inst.ownerJid || '';
            this.phoneNumber = owner.replace('@s.whatsapp.net', '').replace(/^55/, '+55 ');
            this.step = 'connected';
            this.stopPolling();
            // Registrar inbox QR para uso em campanhas BMT
            if (this.inboxId && this.instanceName) {
              BmtEvolutionInboxesApi.register(this.inboxId, this.instanceName).catch(e => {
                console.warn('[BMT] Falha ao registrar inbox QR:', e);
              });
            }
          } else if (this.step === 'qr') {
            await this.fetchQr();
          }
        } else if (this.step === 'qr') {
          await this.fetchQr();
        }
      } catch (e) { /* silent */ }
    },
    startPolling() {
      this.pollingInterval = setInterval(() => this.checkStatus(), 15000);
    },
    stopPolling() {
      if (this.pollingInterval) { clearInterval(this.pollingInterval); this.pollingInterval = null; }
      if (this.countdownInterval) { clearInterval(this.countdownInterval); this.countdownInterval = null; }
    },
    resetCountdown() {
      if (this.countdownInterval) clearInterval(this.countdownInterval);
      this.countdown = 60;
      this.countdownInterval = setInterval(() => {
        this.countdown -= 1;
        if (this.countdown <= 0) { clearInterval(this.countdownInterval); this.fetchQr(); }
      }, 1000);
    },
    goToAgents() {
      router.replace({
        name: 'settings_inboxes_add_agents',
        params: { page: 'new', inbox_id: this.inboxId },
      });
    },
  },
};
</script>

<template>
  <div class="h-full w-full p-6 col-span-6">
    <div v-if="step === 'form'">
      <PageHeader
        :header-title="$t('INBOX_MGMT.ADD.WHATSAPP_QR_CHANNEL.TITLE')"
        :header-content="$t('INBOX_MGMT.ADD.WHATSAPP_QR_CHANNEL.DESC')"
      />
      <form class="flex flex-col gap-4 mt-4" @submit.prevent="createChannel">
        <label>
          {{ $t('INBOX_MGMT.ADD.WHATSAPP_QR_CHANNEL.CHANNEL_NAME.LABEL') }}
          <input
            v-model="channelName"
            type="text"
            :placeholder="$t('INBOX_MGMT.ADD.WHATSAPP_QR_CHANNEL.CHANNEL_NAME.PLACEHOLDER')"
            class="mt-1"
          />
        </label>
        <div class="mt-2">
          <NextButton
            :is-loading="isCreating"
            type="submit"
            solid
            blue
            :label="$t('INBOX_MGMT.ADD.WHATSAPP_QR_CHANNEL.NEXT_BUTTON')"
          />
        </div>
      </form>
    </div>

    <div v-else-if="step === 'qr'" class="flex flex-col items-center gap-6 mt-4">
      <PageHeader
        :header-title="$t('INBOX_MGMT.ADD.WHATSAPP_QR_CHANNEL.SCAN_TITLE')"
        :header-content="$t('INBOX_MGMT.ADD.WHATSAPP_QR_CHANNEL.SCAN_DESC')"
      />
      <div v-if="qrBase64" class="flex flex-col items-center gap-3">
        <img
          :src="'data:image/png;base64,' + qrBase64"
          alt="QR Code WhatsApp"
          style="width:256px;height:256px;border:1px solid #e2e8f0;border-radius:12px;padding:8px;background:#fff;"
        />
        <p class="text-sm text-slate-500">
          {{ $t('INBOX_MGMT.WHATSAPP_QR.EXPIRES_IN', { seconds: countdown }) }}
        </p>
        <NextButton
          :label="$t('INBOX_MGMT.WHATSAPP_QR.REFRESH_BTN')"
          @click="fetchQr"
        />
      </div>
      <div v-else class="flex flex-col items-center gap-3">
        <span class="qr-spinner" />
        <p>{{ $t('INBOX_MGMT.WHATSAPP_QR.LOADING_QR') }}</p>
      </div>
    </div>

    <div v-else-if="step === 'connected'" class="flex flex-col items-center gap-6 mt-8 text-center">
      <h2 class="text-xl font-semibold text-woot-600">{{ $t('INBOX_MGMT.WHATSAPP_QR.CONNECTED_TITLE') }}</h2>
      <p class="text-slate-500">{{ $t('INBOX_MGMT.WHATSAPP_QR.CONNECTED_DESC', { phone: phoneNumber }) }}</p>
      <NextButton
        solid
        blue
        :label="$t('INBOX_MGMT.ADD.WHATSAPP_QR_CHANNEL.CONTINUE_BUTTON')"
        @click="goToAgents"
      />
    </div>
  </div>
</template>

<style scoped>
.qr-spinner {
  display: inline-block;
  width: 3rem;
  height: 3rem;
  border: 3px solid #e2e8f0;
  border-top-color: #1f93ff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
</style>
